import { useQuery } from '@tanstack/react-query';
import { Calendar, DollarSign, Target, TrendingUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/utils/trpc';

interface CategoryAnalyticsProps {
  selectedPeriod?: {
    startDate: Date;
    endDate: Date;
    type: 'current' | 'month' | 'custom';
  };
}

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
}

const COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#95E77E',
  '#FFE66D',
  '#6C5CE7',
  '#00B894',
  '#FDCB6E',
  '#FD79A8',
  '#A29BFE',
  '#B2BEC3',
];

export function CategoryAnalytics({ selectedPeriod }: CategoryAnalyticsProps) {
  const [timeRange, setTimeRange] = useState('30');
  const [transactionType, setTransactionType] = useState<
    'debit' | 'credit' | 'all'
  >('debit');

  // Calculate date range with useMemo to prevent infinite loops
  const { startDate, endDate } = useMemo(() => {
    if (selectedPeriod) {
      return {
        startDate: selectedPeriod.startDate,
        endDate: selectedPeriod.endDate,
      };
    }

    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Number.parseInt(timeRange, 10));
    return { startDate: start, endDate: end };
  }, [selectedPeriod, timeRange]);

  // Fetch category statistics
  const categoryStats = useQuery(
    trpc.transactions.categoryStats.queryOptions({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      type: transactionType,
    })
  );

  // Fetch category trends
  const categoryTrends = useQuery(
    trpc.transactions.categoryTrends.queryOptions({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      period: 'week',
    })
  );

  if (categoryStats.isPending || categoryTrends.isPending) {
    return (
      <div className="space-y-6">
        <div className="h-8 animate-pulse rounded bg-muted" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div className="h-32 animate-pulse rounded bg-muted" key={i} />
          ))}
        </div>
      </div>
    );
  }

  const statsData = categoryStats.data || [];
  const trendsData = categoryTrends.data || [];

  // Prepare data for pie chart
  const pieData = statsData.map((item, index) => ({
    name: item.categoryName,
    value: Math.abs(Number(item.total)),
    color: COLORS[index % COLORS.length],
    percentage: item.percentage,
    count: item.count,
  }));

  // Prepare data for trends chart
  const trendChartData = trendsData.reduce((acc, item) => {
    const period = item.period;
    const categoryName = item.categoryName;
    const existing = acc.find((d) => d.period === period);

    if (existing) {
      existing[categoryName] = Math.abs(Number(item.total));
    } else {
      acc.push({
        period,
        [categoryName]: Math.abs(Number(item.total)),
      });
    }

    return acc;
  }, [] as any[]);

  const totalAmount = statsData.reduce(
    (sum, item) => sum + Math.abs(Number(item.total)),
    0
  );

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
        <div>
          <h2 className="font-bold text-2xl tracking-tight">
            Análise por Categorias
          </h2>
          <p className="text-muted-foreground">
            Visualize seus gastos e receitas organizados por categoria
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Select
            onValueChange={(value: 'debit' | 'credit' | 'all') =>
              setTransactionType(value)
            }
            value={transactionType}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="debit">Gastos</SelectItem>
              <SelectItem value="credit">Receitas</SelectItem>
            </SelectContent>
          </Select>

          {!selectedPeriod && (
            <Select onValueChange={setTimeRange} value={timeRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 dias</SelectItem>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="90">90 dias</SelectItem>
                <SelectItem value="365">1 ano</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {statsData.length === 0 ? (
        <Card>
          <CardContent className="flex h-32 items-center justify-center">
            <p className="text-muted-foreground">
              Nenhuma transação encontrada para o período selecionado.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs className="space-y-6" defaultValue="overview">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Visão Geral</TabsTrigger>
            <TabsTrigger value="distribution">Distribuição</TabsTrigger>
            <TabsTrigger value="trends">Tendências</TabsTrigger>
          </TabsList>

          <TabsContent className="space-y-6" value="overview">
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="font-medium text-sm">Total</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="font-bold text-2xl">
                    {formatCurrency(totalAmount)}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {statsData.reduce((sum, item) => sum + item.count, 0)}{' '}
                    transações
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="font-medium text-sm">
                    Categorias Ativas
                  </CardTitle>
                  <Target className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="font-bold text-2xl">
                    {new Set(statsData.map((item) => item.categoryName)).size}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    categorias utilizadas
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="font-medium text-sm">
                    Categoria Principal
                  </CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="font-bold text-2xl">
                    {statsData[0]?.percentage || 0}%
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {statsData[0]?.categoryName || 'N/A'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Category List */}
            <Card>
              <CardHeader>
                <CardTitle>Detalhamento por Categoria</CardTitle>
                <CardDescription>
                  Lista completa de gastos organizados por categoria
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {statsData.map((item, index) => (
                  <div className="space-y-2" key={item.categoryName}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{
                            backgroundColor: COLORS[index % COLORS.length],
                          }}
                        />
                        <span className="font-medium">{item.categoryName}</span>
                        {transactionType === 'all' && (
                          <Badge
                            className={`text-xs ${
                              item.transactionType === 'credit'
                                ? 'bg-green-500/20 text-green-700 hover:bg-green-500/30 dark:text-green-300'
                                : 'bg-red-500/20 text-red-700 hover:bg-red-500/30 dark:text-red-300'
                            }`}
                            variant={
                              item.transactionType === 'credit'
                                ? 'default'
                                : 'secondary'
                            }
                          >
                            {item.transactionType === 'credit'
                              ? 'Receita'
                              : 'Gasto'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-sm">
                          {formatCurrency(Math.abs(Number(item.total)))}
                        </span>
                        <Badge variant="outline">{item.percentage}%</Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-muted-foreground text-xs">
                      <span>{item.count} transações</span>
                      <span>
                        Média:{' '}
                        {formatCurrency(Math.abs(Number(item.avgAmount)))}
                      </span>
                    </div>
                    <Progress
                      className="h-1"
                      style={{
                        background: `linear-gradient(to right, ${
                          COLORS[index % COLORS.length]
                        }20 0%, ${COLORS[index % COLORS.length]} ${
                          item.percentage
                        }%, #f1f5f9 ${item.percentage}%)`,
                      }}
                      value={item.percentage}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent className="space-y-6" value="distribution">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Distribuição por Categoria</CardTitle>
                  <CardDescription>
                    Visualização proporcional dos gastos por categoria
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer height={300} width="100%">
                    <PieChart>
                      <Pie
                        cx="50%"
                        cy="50%"
                        data={pieData}
                        dataKey="value"
                        fill="#8884d8"
                        label={({ name, percentage }) =>
                          `${name}: ${percentage}%`
                        }
                        labelLine={false}
                        outerRadius={80}
                      >
                        {pieData.map((entry, index) => (
                          <Cell fill={entry.color} key={`cell-${index}`} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Ranking de Categorias</CardTitle>
                  <CardDescription>
                    Comparação dos valores por categoria
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer height={300} width="100%">
                    <BarChart
                      data={pieData.slice(0, 8)}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        angle={-45}
                        dataKey="name"
                        height={60}
                        textAnchor="end"
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                      />
                      <Bar dataKey="value" fill="#6C5CE7" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent className="space-y-6" value="trends">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Tendências por Período
                </CardTitle>
                <CardDescription>
                  Evolução dos gastos por categoria ao longo do tempo
                </CardDescription>
              </CardHeader>
              <CardContent>
                {trendChartData.length > 0 ? (
                  <ResponsiveContainer height={400} width="100%">
                    <LineChart
                      data={trendChartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                      />
                      <Legend />
                      {statsData.slice(0, 6).map((item, index) => (
                        <Line
                          dataKey={item.categoryName}
                          dot={{ r: 4 }}
                          key={item.categoryName}
                          stroke={COLORS[index % COLORS.length]}
                          strokeWidth={2}
                          type="monotone"
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-64 items-center justify-center">
                    <p className="text-muted-foreground">
                      Dados insuficientes para mostrar tendências no período
                      selecionado.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
